/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2021, Bulb Inc.  For more information, please email contact@hellobulb.com.
 */

/**
 * @module bulb-resize/bulbresize
 */

import { Plugin, WidgetResize, type ViewElement } from 'ckeditor5';
import { forEach } from 'lodash-es';
import BulbResizeCommand from './bulbresizecommand';

// TODO CG:
// import '@ckeditor/ckeditor5-image/theme/imageresize.css';

/**
 * A custom implementation of CKEditor's "ImageResize" plugin. Hoping to eventually be able to turn this
 * plugin into a general resize plugin, but may just work with images for now.
 *
 * It adds a possibility to resize images using handles.
 *
 * @extends Plugin
 */
export default class BulbResize extends Plugin {

  /**
   * @inheritDoc
   */
  public static get requires() {
    return [WidgetResize] as const;
  }

  /**
   * @inheritDoc
   */
  public static get pluginName() {
    return 'BulbResize' as const;
  }

  /**
   * @inheritDoc
   */
  public init(): void {
    const { editor } = this;
    this.registerConverters();
    const command = new BulbResizeCommand(editor);
    editor.commands.add('bulbResize', command);

    // eslint-disable-next-line no-restricted-syntax
    for (const blockType of this.blockTypes()) {
      editor.editing.downcastDispatcher.on(`insert:${blockType}`, (evt, data, conversionApi) => {
        const widget = conversionApi.mapper.toViewElement(data.item);
        const resizer = editor.plugins
          .get(WidgetResize)
          .attachTo({
            unit: editor.config.get('resizeUnit') as any || '%',
            modelElement: data.item,
            viewElement: widget,
            editor,

            /**
             * This method returns the widget element which need to be register for resizing.
             * As we have different types of blocks, so we are using switch case based on block type
             * to get the correct target element.
             *
             * @param domWidgetElement
             * @returns - target block element.
             */
            getHandleHost(domWidgetElement) {
              let block;
              switch (blockType) {
                case 'image': {
                  const imageElement = domWidgetElement.querySelector('.bulb-image-asset');
                  block = imageElement || domWidgetElement;
                  break;
                }

                case 'audio':
                case 'video': {
                  const isVimeo = domWidgetElement.querySelector('.bulb_vimeo_wrapper');
                  block = (isVimeo) || domWidgetElement.querySelector('.bulb_jwplayer_wrapper');
                  break;
                }

                case 'attachedFile':
                  block = domWidgetElement.querySelector('.attached-file-wrapper');
                  break;
                case 'embed':
                  block = domWidgetElement.querySelector('.bulb_iframely_wrapper');
                  break;
                default:
                  block = domWidgetElement;
              }
              return block;
            },

            /**
             * Return the container widget element which host the resizing.
             * @param domWidgetElement
             * @returns host element
             */
            getResizeHost(domWidgetElement) {
              return domWidgetElement;
            },

            /**
             * Return true if the block is aligned center.
             * @returns {boolean}
             */
            isCentered(): boolean {
              const blockAlignment = data.item.getAttribute('blockAlignment');
              return !blockAlignment || blockAlignment === 'full' || blockAlignment === 'alignCenter';
            },

            /**
             * A callback to be executed once the resizing process is done.
             * @param newValue
             */
            onCommit(newValue) {
              if (blockType === 'video' || blockType === 'embed') {
                const resizeContainerElement = document.getElementById(
                  `resize-${widget.getAttribute('bulb-block-id')}`,
                );
                if (resizeContainerElement) {
                  resizeContainerElement.style.display = 'none';
                }
              }
              editor.execute('bulbResize', { width: newValue });
            },
          });

        /**
         * Updates the proposed size based on domEventData.
         */
        resizer.on('updateSize', () => {
          if (blockType === 'video' || blockType === 'embed') {
            const figureElement = document.querySelectorAll(
              `[bulb-block-id="${widget.getAttribute('bulb-block-id')}"]`,
            )[0];
            if (figureElement) {
              const resizerElement = figureElement.getElementsByClassName('ck-widget__resizer')[0] as HTMLElement;
              const style = resizerElement.style.cssText;
              const resizeContainerElement = document.getElementById(`resize-${widget.getAttribute('bulb-block-id')}`);
              if (resizeContainerElement && !this.isEmptyString(style)) {
                Object.assign(resizeContainerElement.style, this.parseStyleString(style));
                resizeContainerElement.style.display = 'block';
              }
            }
          }

          if (!widget.hasClass('block-resized')) {
            editor.editing.view.change((writer) => {
              writer.addClass('block-resized', widget);
            });
          }
        });

        resizer.bind('isEnabled').to(command);
      }, { priority: 'low' });
    }
  }

  /**
   * Register resize converters for different block types.
   *
   * @private
   */
  private registerConverters() {
    const { editor } = this;

    editor.conversion.for('downcast').add((dispatcher) => {
      // eslint-disable-next-line no-restricted-syntax
      forEach(this.blockTypes(), (blockType) => {
        dispatcher.on(`attribute:width:${blockType}`, (evt, data, conversionApi) => {
          if (!conversionApi.consumable.consume(data.item, evt.name)) {
            return;
          }

          const viewWriter = conversionApi.writer;
          const figure = conversionApi.mapper.toViewElement(data.item);

          if (data.attributeNewValue !== null) {
            viewWriter.setStyle('width', data.attributeNewValue, figure);
            viewWriter.addClass('block-resized', figure);
          } else {
            viewWriter.removeStyle('width', figure);
            viewWriter.removeClass('block-resized', figure);
          }
        });
      });
    });

    editor.conversion.for('upcast')
      .attributeToAttribute({
        view: {
          name: 'figure',
          styles: {
            width: /.+/,
          },
        },
        model: {
          key: 'width',
          value: (viewElement: ViewElement) => viewElement.getStyle('width'),
        },
      });
  }

  /**
   * Returns array of block types which support resizing.
   * @returns {string[]}
   * @private
   */
  private blockTypes(): string[] {
    return ['image', 'video', 'audio', 'attachedFile', 'embed'];
  }

  /**
   * Parses a CSS-style string into an object with key-value pairs.
   *
   * @param {string} styleString - A string containing CSS properties (e.g., "height: 340px, width: 300px").
   * @returns {Record<string, string>} - An object representing the parsed CSS properties.
   *
   * @example
   * parseStyleString("height: 340px, width: 300px");
   * // Returns: { height: "340px", width: "300px" }
   *
   * @example
   * parseStyleString("margin: 10px");
   * // Returns: { margin: "10px" }
   */
  private parseStyleString(styleString: string): Record<string, string> {
    if (!styleString || styleString.trim() === '') {
      return {}; // Return an empty object if input is empty or only spaces
    }

    return styleString.split(';').reduce((acc, style) => {
      const [key, value] = style.split(':').map((s) => s.trim());
      if (key && value) {
        acc[key] = value;
      }
      return acc;
    }, {} as Record<string, string>);
  }

  /**
   * Checks if a given string is empty, null, or undefined.
   *
   * @param {string | null | undefined} value - The input string to check.
   * @returns {boolean} - Returns `true` if the string is empty, null, or undefined, otherwise `false`.
   *
   * @example
   * isEmptyString(null); // true
   * isEmptyString(undefined); // true
   * isEmptyString(""); // true
   * isEmptyString("   "); // true
   * isEmptyString("hello"); // false
   */
  private isEmptyString(value: string | null | undefined): boolean {
    return !value || value.trim() === '';
  }

}

/**
 * The available options are `'px'` or `'%'`.
 *
 * Determines the size unit applied to the resized image.
 *
 *    ClassicEditor
 *      .create( editorElement, {
 *        image: {
 *          resizeUnit: 'px'
 *        }
 *      } )
 *      .then( ... )
 *      .catch( ... );
 *
 *
 * This option is used by the {@link module:image/imageresize~ImageResize} feature.
 *
 * @default '%'
 * @member {String} module:image/image~ImageConfig#resizeUnit
 */
